import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { 
  Loader2, CalendarIcon, ChevronRight, ChevronLeft, Plus, Minus, 
  Check, ShoppingCart, AlertCircle, Leaf, Mail, Clock, RotateCcw, Gift, Info, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { format, nextMonday, isMonday, setHours, setMinutes, getDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import ClientLayout from '../components/ClientLayout';
import { cn } from '../lib/utils';

const API_URL = window.location.origin;
const UK_TIMEZONE = 'Europe/London';
const STEP_LABELS = ['Order Details', 'Monday', 'Wednesday', "Add-on's", 'Confirmation'];

const OrderForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [addons, setAddons] = useState({ basic: [], premium: [] });
  
  // Form state
  const [deliveryWeek, setDeliveryWeek] = useState(null);
  const [menuWeek, setMenuWeek] = useState(null);
  const [selectedMondayCategory, setSelectedMondayCategory] = useState(null);
  const [selectedWednesdayCategory, setSelectedWednesdayCategory] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [orderNotes, setOrderNotes] = useState('');
  
  // Add-on state
  const [addonPreference, setAddonPreference] = useState('basic'); // for Large Meals
  const [addonQuantities, setAddonQuantities] = useState({}); // {addonName: qty}
  const [additionalAddonQuantities, setAdditionalAddonQuantities] = useState({}); // extra beyond allowance
  
  // Repeat order state
  const [repeatSuggestion, setRepeatSuggestion] = useState(null);
  const [loadingRepeat, setLoadingRepeat] = useState(false);
  const [reorderApplied, setReorderApplied] = useState(false);
  const [reorderMenuWeek, setReorderMenuWeek] = useState(null);
  
  // Edit order state
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [editApplied, setEditApplied] = useState(false);

  const cateringPlan = user?.catering_plan || 'Small Meals';

  // Menu week calculation (mirrors backend logic)
  const CYCLE_START = new Date(2026, 2, 30); // March 30, 2026
  const getMenuWeekForDate = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffMs = d - CYCLE_START;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const weekOffset = Math.floor(diffDays / 7);
    return ((weekOffset % 3) + 3) % 3 + 1; // handle negatives safely
  };

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [catRes, prodRes, addonRes] = await Promise.all([
        axios.get(`${API_URL}/api/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/products`, { withCredentials: true }),
        axios.get(`${API_URL}/api/addons`, { withCredentials: true })
      ]);
      setCategories(catRes.data);
      setProducts(prodRes.data);
      setAddons(addonRes.data);
      
      const mondayCats = catRes.data.filter(c => c.delivery_day === 'Monday' || c.delivery_day === 'Both');
      const wednesdayCats = catRes.data.filter(c => c.delivery_day === 'Wednesday' || c.delivery_day === 'Both');
      if (mondayCats.length > 0) setSelectedMondayCategory(mondayCats[0].id);
      if (wednesdayCats.length > 0) setSelectedWednesdayCategory(wednesdayCats[0].id);
    } catch (e) {
      // Error handled silently
      toast.error('Failed to load menu data');
    } finally {
      setLoading(false);
    }
  };

  // Apply reorder data from Account Order History
  useEffect(() => {
    if (reorderApplied || loading || !products.length) return;
    const reorderData = location.state?.reorderFrom;
    if (!reorderData) return;

    const newQuantities = {};
    (reorderData.items || []).forEach(item => {
      if (item.product_id) {
        const product = products.find(p => p.id === item.product_id);
        if (product) {
          newQuantities[item.product_id] = item.quantity;
        }
      }
    });
    setQuantities(newQuantities);

    if (reorderData.addon_preference) {
      setAddonPreference(reorderData.addon_preference);
    }
    const newAddonQtys = {};
    const newAdditionalQtys = {};
    (reorderData.addon_selections || []).forEach(sel => {
      if (sel.is_additional) {
        newAdditionalQtys[sel.addon_name] = sel.quantity;
      } else {
        newAddonQtys[sel.addon_name] = sel.quantity;
      }
    });
    setAddonQuantities(newAddonQtys);
    setAdditionalAddonQuantities(newAdditionalQtys);

    if (reorderData.notes) {
      setOrderNotes(reorderData.notes);
    }

    setReorderApplied(true);
    setReorderMenuWeek(reorderData.menu_week || null);
    toast.success(`Previous order loaded (Week ${reorderData.menu_week || '?'})! Only matching delivery weeks are available.`);

    // Clear the navigation state so refreshing doesn't re-apply
    window.history.replaceState({}, document.title);
  }, [loading, products, location.state, reorderApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply edit order data from Account Order History
  useEffect(() => {
    if (editApplied || loading || !products.length) return;
    const editData = location.state?.editOrder;
    if (!editData) return;

    // Set delivery week and menu week
    if (editData.delivery_week) {
      const dw = new Date(editData.delivery_week + 'T00:00:00');
      setDeliveryWeek(dw);
      setMenuWeek(getMenuWeekForDate(dw));
    }

    const newQuantities = {};
    (editData.items || []).forEach(item => {
      if (item.product_id) {
        const product = products.find(p => p.id === item.product_id);
        if (product) {
          newQuantities[item.product_id] = item.quantity;
        }
      }
    });
    setQuantities(newQuantities);

    if (editData.addon_preference) {
      setAddonPreference(editData.addon_preference);
    }
    const newAddonQtys = {};
    const newAdditionalQtys = {};
    (editData.addon_selections || []).forEach(sel => {
      if (sel.is_additional) {
        newAdditionalQtys[sel.addon_name] = sel.quantity;
      } else {
        newAddonQtys[sel.addon_name] = sel.quantity;
      }
    });
    setAddonQuantities(newAddonQtys);
    setAdditionalAddonQuantities(newAdditionalQtys);

    if (editData.notes) {
      setOrderNotes(editData.notes);
    }

    setEditingOrderId(editData.id);
    setEditApplied(true);
    toast.info('Editing your order. Make changes and submit to update.');

    window.history.replaceState({}, document.title);
  }, [loading, products, location.state, editApplied]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch menu week and repeat suggestion when delivery week changes
  const fetchMenuWeek = useCallback(async (date) => {
    if (!date) return;
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      const { data } = await axios.get(`${API_URL}/api/menu-week?date=${dateStr}`, { withCredentials: true });
      setMenuWeek(data.menu_week);
    } catch (e) {
      // Error handled silently
    }
  }, []);

  const fetchRepeatSuggestion = useCallback(async (date) => {
    if (!date) return;
    setLoadingRepeat(true);
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      const { data } = await axios.get(`${API_URL}/api/orders/repeat-suggestion?delivery_week=${dateStr}`, { withCredentials: true });
      setRepeatSuggestion(data.found ? data.source_order : null);
    } catch (e) {
      // Error handled silently
    } finally {
      setLoadingRepeat(false);
    }
  }, []);

  useEffect(() => {
    if (deliveryWeek) {
      fetchMenuWeek(deliveryWeek);
      fetchRepeatSuggestion(deliveryWeek);
    }
  }, [deliveryWeek, fetchMenuWeek, fetchRepeatSuggestion]);

  // Filter categories by delivery day
  const mondayCategories = useMemo(() => 
    categories.filter(c => c.delivery_day === 'Monday' || c.delivery_day === 'Both'), [categories]);

  const wednesdayCategories = useMemo(() => 
    categories.filter(c => c.delivery_day === 'Wednesday' || c.delivery_day === 'Both'), [categories]);

  // Cutoff logic
  const getEarliestAvailableMonday = () => {
    const nowUTC = new Date();
    const nowUK = toZonedTime(nowUTC, UK_TIMEZONE);
    const dayOfWeek = getDay(nowUK);
    const thursdayCutoff = setMinutes(setHours(nowUK, 14), 0);
    let targetMonday = nextMonday(nowUK);
    if (dayOfWeek === 4 && nowUK >= thursdayCutoff) {
      targetMonday = nextMonday(targetMonday);
    } else if (dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0) {
      targetMonday = nextMonday(targetMonday);
    }
    return new Date(targetMonday.getFullYear(), targetMonday.getMonth(), targetMonday.getDate());
  };

  const isValidOrderingMonday = (date) => {
    if (!isMonday(date)) return false;
    const earliestMonday = getEarliestAvailableMonday();
    const dateAtMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (dateAtMidnight < earliestMonday) return false;
    // When reordering, restrict to Mondays matching the same menu week
    if (reorderMenuWeek) {
      return getMenuWeekForDate(dateAtMidnight) === reorderMenuWeek;
    }
    return true;
  };

  const getCutoffInfo = () => {
    const nowUTC = new Date();
    const nowUK = toZonedTime(nowUTC, UK_TIMEZONE);
    const dayOfWeek = getDay(nowUK);
    const thursdayCutoff = setMinutes(setHours(nowUK, 14), 0);
    if (dayOfWeek === 4 && nowUK < thursdayCutoff) {
      const hoursLeft = Math.floor((thursdayCutoff - nowUK) / (1000 * 60 * 60));
      const minsLeft = Math.floor(((thursdayCutoff - nowUK) % (1000 * 60 * 60)) / (1000 * 60));
      return { urgent: true, message: `Order by 2pm today (UK time) for next week delivery (${hoursLeft}h ${minsLeft}m remaining)` };
    }
    return { urgent: false, message: "Orders must be placed by 2pm Thursday for guaranteed next week delivery" };
  };

  const cutoffInfo = getCutoffInfo();

  // Products filtering - respect rotating category menu weeks
  const filterByMenuWeek = useCallback((productsList) => {
    if (!menuWeek) return productsList;
    return productsList.filter(p => {
      const category = categories.find(c => c.id === p.category_id);
      if (category?.rotation_type === 'Rotating') {
        // Only show if product is assigned to this menu week (or has no weeks = show always)
        return !p.menu_weeks?.length || p.menu_weeks.includes(menuWeek);
      }
      return true; // Fixed categories always show
    });
  }, [menuWeek, categories]);

  const getMondayProducts = useMemo(() => {
    const raw = selectedMondayCategory ? products.filter(p => p.category_id === selectedMondayCategory) : [];
    return filterByMenuWeek(raw);
  }, [products, selectedMondayCategory, filterByMenuWeek]);

  const getWednesdayProducts = useMemo(() => {
    const raw = selectedWednesdayCategory ? products.filter(p => p.category_id === selectedWednesdayCategory) : [];
    return filterByMenuWeek(raw);
  }, [products, selectedWednesdayCategory, filterByMenuWeek]);

  // Items calculation
  const getMondayItems = useMemo(() => {
    const mondayCatIds = mondayCategories.map(c => c.id);
    return Object.entries(quantities)
      .filter(([pid, qty]) => {
        const product = products.find(p => p.id === pid);
        return product && mondayCatIds.includes(product.category_id) && qty > 0;
      })
      .map(([pid, quantity]) => {
        const product = products.find(p => p.id === pid);
        const category = categories.find(c => c.id === product?.category_id);
        return { product_id: pid, product_name: product?.name || '', quantity, category_name: category?.name || '', meal_type_tag: product?.meal_type_tag || '', delivery_day: 'Monday' };
      });
  }, [quantities, products, categories, mondayCategories]);

  const getWednesdayItems = useMemo(() => {
    const wednesdayCatIds = wednesdayCategories.map(c => c.id);
    return Object.entries(quantities)
      .filter(([pid, qty]) => {
        const product = products.find(p => p.id === pid);
        return product && wednesdayCatIds.includes(product.category_id) && qty > 0;
      })
      .map(([pid, quantity]) => {
        const product = products.find(p => p.id === pid);
        const category = categories.find(c => c.id === product?.category_id);
        return { product_id: pid, product_name: product?.name || '', quantity, category_name: category?.name || '', meal_type_tag: product?.meal_type_tag || '', delivery_day: 'Wednesday' };
      });
  }, [quantities, products, categories, wednesdayCategories]);

  const mondayItemCount = useMemo(() => getMondayItems.reduce((sum, i) => sum + i.quantity, 0), [getMondayItems]);
  const wednesdayItemCount = useMemo(() => getWednesdayItems.reduce((sum, i) => sum + i.quantity, 0), [getWednesdayItems]);
  const totalItems = mondayItemCount + wednesdayItemCount;

  // Chilled/Hybrid meal count (for add-on calculation)
  const chilledHybridCount = useMemo(() => {
    const allItems = [...getMondayItems, ...getWednesdayItems];
    return allItems.reduce((sum, item) => {
      const category = categories.find(c => c.name === item.category_name);
      if (category?.meal_type === 'Chilled/Hybrid') {
        return sum + item.quantity;
      }
      return sum;
    }, 0);
  }, [getMondayItems, getWednesdayItems, categories]);

  // Add-on allowance calculation
  const includedAddonAllowance = useMemo(() => {
    if (cateringPlan === 'Small Meals') {
      return { type: 'basic', count: chilledHybridCount * 1 };
    } else {
      if (addonPreference === 'premium') {
        return { type: 'premium', count: chilledHybridCount * 1 };
      }
      return { type: 'basic', count: chilledHybridCount * 2 };
    }
  }, [cateringPlan, chilledHybridCount, addonPreference]);

  const totalIncludedSelected = useMemo(() => 
    Object.values(addonQuantities).reduce((sum, qty) => sum + qty, 0), [addonQuantities]);

  // Auto-clamp: trim included add-on quantities when allowance shrinks (e.g. user goes back and removes meals)
  useEffect(() => {
    const total = Object.values(addonQuantities).reduce((s, q) => s + q, 0);
    if (total > includedAddonAllowance.count) {
      const entries = Object.entries(addonQuantities);
      const clamped = {};
      let budget = includedAddonAllowance.count;
      for (const [name, qty] of entries) {
        if (budget <= 0) break;
        const keep = Math.min(qty, budget);
        clamped[name] = keep;
        budget -= keep;
      }
      setAddonQuantities(clamped);
    }
  }, [includedAddonAllowance.count]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalAdditionalSelected = useMemo(() => 
    Object.values(additionalAddonQuantities).reduce((sum, qty) => sum + qty, 0), [additionalAddonQuantities]);

  // Handlers
  const handleQuantityChange = (productId, delta) => {
    setQuantities(prev => {
      const current = prev[productId] || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) { const { [productId]: _, ...rest } = prev; return rest; }
      return { ...prev, [productId]: newQty };
    });
  };

  const handleQuantityInput = (productId, value) => {
    const parsed = parseInt(value, 10);
    if (value === '' || value === '0') {
      setQuantities(prev => { const { [productId]: _, ...rest } = prev; return rest; });
      return;
    }
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 9999) {
      setQuantities(prev => ({ ...prev, [productId]: parsed }));
    }
  };

  const handleAddonQuantityChange = (addonName, delta) => {
    const remaining = includedAddonAllowance.count - totalIncludedSelected;
    setAddonQuantities(prev => {
      const current = prev[addonName] || 0;
      const newQty = Math.max(0, current + delta);
      if (delta > 0 && remaining <= 0) return prev;
      if (newQty === 0) { const { [addonName]: _, ...rest } = prev; return rest; }
      return { ...prev, [addonName]: newQty };
    });
  };

  const handleAddonQuantityInput = (addonName, value) => {
    const parsed = parseInt(value, 10);
    if (value === '' || value === '0') {
      setAddonQuantities(prev => { const { [addonName]: _, ...rest } = prev; return rest; });
      return;
    }
    if (!isNaN(parsed) && parsed >= 0) {
      const otherTotal = Object.entries(addonQuantities).filter(([k]) => k !== addonName).reduce((s, [, v]) => s + v, 0);
      const maxForThis = includedAddonAllowance.count - otherTotal;
      const clamped = Math.min(parsed, maxForThis);
      if (clamped > 0) {
        setAddonQuantities(prev => ({ ...prev, [addonName]: clamped }));
      } else {
        setAddonQuantities(prev => { const { [addonName]: _, ...rest } = prev; return rest; });
      }
    }
  };

  const handleAdditionalAddonChange = (addonName, delta) => {
    setAdditionalAddonQuantities(prev => {
      const current = prev[addonName] || 0;
      const newQty = Math.max(0, current + delta);
      if (newQty === 0) { const { [addonName]: _, ...rest } = prev; return rest; }
      return { ...prev, [addonName]: newQty };
    });
  };

  const handleAdditionalAddonInput = (addonName, value) => {
    const parsed = parseInt(value, 10);
    if (value === '' || value === '0') {
      setAdditionalAddonQuantities(prev => { const { [addonName]: _, ...rest } = prev; return rest; });
      return;
    }
    if (!isNaN(parsed) && parsed >= 0 && parsed <= 9999) {
      setAdditionalAddonQuantities(prev => ({ ...prev, [addonName]: parsed }));
    }
  };

  // Count selected types for validation
  const selectedBasicTypes = Object.entries(addonQuantities)
    .filter(([name, qty]) => qty > 0 && addons.basic.includes(name)).length;
  const selectedPremiumTypes = Object.entries(addonQuantities)
    .filter(([name, qty]) => qty > 0 && addons.premium.includes(name)).length;
  const additionalBasicTypes = Object.entries(additionalAddonQuantities)
    .filter(([name, qty]) => qty > 0 && addons.basic.includes(name)).length;
  const additionalPremiumTypes = Object.entries(additionalAddonQuantities)
    .filter(([name, qty]) => qty > 0 && addons.premium.includes(name)).length;

  // Repeat order handler
  const applyRepeatOrder = () => {
    if (!repeatSuggestion) return;
    
    // Map items by product_id
    const newQuantities = {};
    repeatSuggestion.items.forEach(item => {
      const product = products.find(p => p.id === item.product_id);
      if (product) {
        newQuantities[item.product_id] = item.quantity;
      }
    });
    setQuantities(newQuantities);
    
    // Apply addon selections
    if (repeatSuggestion.addon_preference) {
      setAddonPreference(repeatSuggestion.addon_preference);
    }
    const newAddonQtys = {};
    const newAdditionalQtys = {};
    (repeatSuggestion.addon_selections || []).forEach(sel => {
      if (sel.is_additional) {
        newAdditionalQtys[sel.addon_name] = sel.quantity;
      } else {
        newAddonQtys[sel.addon_name] = sel.quantity;
      }
    });
    setAddonQuantities(newAddonQtys);
    setAdditionalAddonQuantities(newAdditionalQtys);
    
    if (repeatSuggestion.notes) {
      setOrderNotes(repeatSuggestion.notes);
    }
    
    toast.success('Previous order loaded! Review and adjust as needed.');
    setStep(2);
  };

  // Build addon selections for submission
  const buildAddonSelections = () => {
    const selections = [];
    // Included add-ons
    const includedType = includedAddonAllowance.type;
    const includedList = includedType === 'basic' ? addons.basic : addons.premium;
    Object.entries(addonQuantities).forEach(([name, qty]) => {
      if (qty > 0 && includedList.includes(name)) {
        selections.push({ addon_name: name, addon_type: includedType, quantity: qty, is_additional: false });
      }
    });
    // Additional add-ons
    Object.entries(additionalAddonQuantities).forEach(([name, qty]) => {
      if (qty > 0) {
        const type = addons.basic.includes(name) ? 'basic' : 'premium';
        selections.push({ addon_name: name, addon_type: type, quantity: qty, is_additional: true });
      }
    });
    return selections;
  };

  const handleSubmitOrder = async () => {
    if (totalItems === 0) {
      toast.error('Please add at least one item to your order');
      return;
    }

    // Validate included add-ons don't exceed allowance
    if (totalIncludedSelected > includedAddonAllowance.count) {
      toast.error(`You have ${totalIncludedSelected} included add-ons but your allowance is ${includedAddonAllowance.count}. Please reduce your add-on selections.`);
      setStep(4);
      return;
    }
    
    // Pre-flight session check — catch expired tokens before attempting submission
    try {
      await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true, timeout: 10000 });
    } catch {
      toast.error('Your session has expired. Please log in again.');
      navigate('/login');
      return;
    }
    
    setSubmitting(true);
    try {
      const allItems = [...getMondayItems, ...getWednesdayItems];
      const orderData = {
        delivery_week: deliveryWeek ? format(deliveryWeek, 'yyyy-MM-dd') : '',
        menu_week: menuWeek,
        catering_plan: cateringPlan,
        addon_preference: cateringPlan === 'Large Meals' ? addonPreference : 'basic',
        hte_meal_type: user?.hte_meal_type || '',
        items: allItems,
        addon_selections: buildAddonSelections(),
        notes: orderNotes
      };
      
      if (editingOrderId) {
        await axios.put(`${API_URL}/api/orders/${editingOrderId}`, orderData, { withCredentials: true, timeout: 30000 });
        toast.success('Order updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/orders`, orderData, { withCredentials: true, timeout: 30000 });
        toast.success('Order submitted successfully!');
      }
      navigate('/account');
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      
      if (!e.response) {
        // Network error / timeout
        toast.error('Unable to reach the server. Please check your connection and try again.');
      } else if (status === 401) {
        // Session expired
        toast.error('Your session has expired. Please log in again.');
        navigate('/login');
        return;
      } else if (status === 422) {
        // Validation error - stale product data
        toast.error('Some items in your order may no longer be available. Please refresh the page and try again.');
      } else if (detail && detail.includes('deadline')) {
        toast.error(detail);
      } else {
        toast.error(detail || (editingOrderId ? 'Failed to update order. Please try again.' : 'Failed to submit order. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getMealTypeBadgeClass = (mealType) => {
    if (mealType?.includes('Chilled')) return 'badge-chilled';
    if (mealType?.includes('Hybrid')) return 'badge-hybrid';
    if (mealType?.includes('Heat')) return 'badge-hte';
    return '';
  };

  // Render product grid
  const renderProductGrid = (productsList) => (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
      {productsList.map((product) => (
        <Card key={product.id} className="meal-card border-slate-200/60 overflow-hidden" data-testid={`product-card-${product.id}`}>
          <div className="aspect-video relative overflow-hidden bg-slate-100">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'; }} />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">No image</div>
            )}
            {product.is_vegetarian && (
              <Badge className="absolute top-2 right-2 badge-vegetarian"><Leaf className="w-3 h-3 mr-1" />V</Badge>
            )}
          </div>
          <CardContent className="p-3 sm:p-4">
            <h3 className="font-medium text-slate-900 mb-2 line-clamp-2 text-sm">{product.name}</h3>
            <div className="flex items-center justify-between mb-3">
              <Badge className={`${getMealTypeBadgeClass(product.meal_type_tag)} text-xs`}>{product.meal_type_tag}</Badge>
            </div>
            <div className="flex items-center justify-center gap-2 sm:gap-3">
              <button onClick={() => handleQuantityChange(product.id, -1)} className="quantity-btn" disabled={!quantities[product.id]} data-testid={`qty-decrease-${product.id}`}>
                <Minus className="w-4 h-4" />
              </button>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={quantities[product.id] || 0}
                onChange={(e) => handleQuantityInput(product.id, e.target.value)}
                className="w-14 text-center font-semibold text-lg border border-slate-200 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                data-testid={`qty-input-${product.id}`} />
              <button onClick={() => handleQuantityChange(product.id, 1)} className="quantity-btn" data-testid={`qty-increase-${product.id}`}>
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  // Render add-on row
  const renderAddonRow = (addonName, qty, onChange, onInput, type, maxTypes, currentTypes, isInList) => {
    const isSelected = qty > 0;
    const canAddType = isSelected || currentTypes < maxTypes;
    
    return (
      <div key={addonName} className={`flex items-center justify-between p-3 rounded-xl border transition-all ${
        isSelected ? 'border-green-300 bg-green-50/50' : 'border-slate-200'
      }`}>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900">{addonName}</p>
          <p className="text-xs text-slate-500">{type === 'basic' ? 'Basic' : 'Premium'} add-on</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => onChange(addonName, -1)} className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-100 disabled:opacity-30" disabled={!qty}>
            <Minus className="w-3 h-3" />
          </button>
          <input type="text" inputMode="numeric" pattern="[0-9]*" value={qty || 0}
            onChange={(e) => onInput(addonName, e.target.value)}
            className="w-12 text-center font-semibold text-sm border border-slate-200 rounded-lg py-1 focus:outline-none focus:ring-2 focus:ring-green-500"
            disabled={!canAddType && !isSelected} />
          <button onClick={() => {
            if (!canAddType && !isSelected) {
              toast.error(`Maximum ${maxTypes} ${type} add-on type(s) allowed`);
              return;
            }
            onChange(addonName, 1);
          }} className="w-8 h-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-100 disabled:opacity-30">
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 animate-fade-in">
        {/* Progress Indicator */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-1 sm:gap-3">
            {STEP_LABELS.map((label, idx) => {
              const stepNum = idx + 1;
              return (
                <React.Fragment key={label}>
                  {idx > 0 && <div className={`w-4 sm:w-8 h-0.5 ${step > idx ? 'bg-green-600' : 'bg-slate-200'}`} />}
                  <div className={`flex items-center gap-1 sm:gap-2 ${step >= stepNum ? 'text-green-600' : 'text-slate-400'}`}>
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm flex-shrink-0 ${
                      step > stepNum ? 'bg-green-600 text-white' : step === stepNum ? 'bg-green-600 text-white' : 'bg-slate-200'
                    }`}>
                      {step > stepNum ? <Check className="w-3 h-3 sm:w-4 sm:h-4" /> : stepNum}
                    </div>
                    <span className="font-medium hidden md:inline text-sm">{label}</span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Step 1: Order Details */}
        {step === 1 && (
          <div className="max-w-2xl mx-auto space-y-6">
            <Card className="border-slate-200/60 shadow-sm">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Select Delivery Week</CardTitle>
                <CardDescription>Choose the week you'd like your meals delivered</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Cutoff Notice */}
                <div className={`rounded-xl p-4 flex items-start gap-3 ${
                  cutoffInfo.urgent ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-200'
                }`}>
                  <Clock className={`w-5 h-5 mt-0.5 flex-shrink-0 ${cutoffInfo.urgent ? 'text-amber-600' : 'text-slate-500'}`} />
                  <div>
                    <p className={`text-sm font-medium ${cutoffInfo.urgent ? 'text-amber-800' : 'text-slate-700'}`}>{cutoffInfo.message}</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Need to order after the cutoff?{' '}
                      <a href="mailto:hello@munchdmeals.com" className="text-green-600 hover:text-green-700 font-medium inline-flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />Contact us
                      </a>
                    </p>
                  </div>
                </div>

                {/* Reorder Week Restriction Notice */}
                {reorderMenuWeek && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-testid="reorder-week-notice">
                    <RotateCcw className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Reorder Mode — Week {reorderMenuWeek} Only</p>
                      <p className="text-xs text-blue-700 mt-1">
                        Your reorder contains items from Menu Week {reorderMenuWeek}. Only delivery dates matching this rotation are selectable to ensure all menu items are available.
                      </p>
                    </div>
                  </div>
                )}

                {/* Edit Order Notice */}
                {editingOrderId && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="edit-order-notice">
                    <Pencil className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">Editing Order</p>
                      <p className="text-xs text-amber-700 mt-1">
                        You are editing an existing order. The delivery week is locked. Update your items and submit to save changes.
                      </p>
                    </div>
                  </div>
                )}

                {/* Delivery Week Calendar */}
                <div className="space-y-2">
                  <label className="text-base font-medium text-slate-900">Delivery Week *</label>
                  {editingOrderId ? (
                    <div className="w-full rounded-xl h-12 border border-slate-200 bg-slate-50 flex items-center px-3 text-slate-700" data-testid="delivery-week-locked">
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {deliveryWeek ? (
                        <span>
                          {format(deliveryWeek, "EEEE, d MMMM yyyy")}
                          {menuWeek && <span className="ml-2 text-green-600 font-medium">(Menu Week {menuWeek})</span>}
                        </span>
                      ) : "Locked"}
                      <span className="ml-auto text-xs text-slate-400">Locked for editing</span>
                    </div>
                  ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal rounded-xl h-12", !deliveryWeek && "text-muted-foreground")} data-testid="delivery-week-picker">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {deliveryWeek ? (
                          <span>
                            {format(deliveryWeek, "EEEE, d MMMM yyyy")}
                            {menuWeek && <span className="ml-2 text-green-600 font-medium">(Menu Week {menuWeek})</span>}
                          </span>
                        ) : "Select a Monday"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={deliveryWeek}
                        onSelect={(date) => { if (date && isValidOrderingMonday(date)) setDeliveryWeek(date); }}
                        disabled={(date) => !isValidOrderingMonday(date)} initialFocus />
                    </PopoverContent>
                  </Popover>
                  )}
                </div>

                {/* Menu Week Badge */}
                {menuWeek && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span className="text-sm text-green-800">This is <strong>Menu Week {menuWeek}</strong> of the 3-week rolling cycle</span>
                  </div>
                )}

                {/* Repeat Order Suggestion */}
                {deliveryWeek && !loadingRepeat && repeatSuggestion && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <RotateCcw className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-900 text-sm">Repeat a previous order?</p>
                        <p className="text-xs text-blue-700 mt-1">
                          We found your last Menu Week {menuWeek} order from {format(new Date(repeatSuggestion.delivery_week), 'd MMM yyyy')} ({repeatSuggestion.items?.length} items)
                        </p>
                        <Button onClick={applyRepeatOrder} variant="outline" size="sm" className="mt-3 rounded-full border-blue-300 text-blue-700 hover:bg-blue-100" data-testid="repeat-order-button">
                          <RotateCcw className="w-3 h-3 mr-2" />
                          Load Previous Order
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                {deliveryWeek && loadingRepeat && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />Checking for previous orders...
                  </div>
                )}

                {/* Account Info */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <p className="text-sm font-medium text-slate-700">Your Account Settings:</p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-sm">{cateringPlan}</Badge>
                    {user?.hte_meal_type && <Badge variant="outline" className="text-sm">{user.hte_meal_type}</Badge>}
                  </div>
                  <p className="text-xs text-slate-500">Update these in <a href="/account" className="text-green-600 hover:underline">My Account</a></p>
                </div>

                <div className="pt-4">
                  <Button onClick={() => setStep(2)} disabled={!deliveryWeek}
                    className="w-full rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200" data-testid="proceed-to-step-2">
                    Continue to Monday Meals<ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 2: Monday Meals */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-600" data-testid="back-to-step-1">
                <ChevronLeft className="w-4 h-4 mr-1 sm:mr-2" />Back
              </Button>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="text-right">
                  <p className="text-xs sm:text-sm text-slate-500">Monday items</p>
                  <p className="text-lg sm:text-xl font-semibold text-slate-900">{mondayItemCount}</p>
                </div>
                <Button onClick={() => setStep(3)} className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200 text-sm" data-testid="proceed-to-step-3">
                  Wednesday<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-blue-700 font-semibold">M</span>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Select Meals - Monday Delivery</h2>
                <p className="text-sm text-slate-500">{deliveryWeek ? format(deliveryWeek, "EEEE, d MMMM yyyy") : ''}</p>
              </div>
            </div>

            <div className="overflow-x-auto pb-2">
              <div className="flex gap-2 min-w-max">
                {mondayCategories.map((cat) => (
                  <Button key={cat.id} variant={selectedMondayCategory === cat.id ? "default" : "outline"}
                    onClick={() => setSelectedMondayCategory(cat.id)}
                    className={`rounded-full whitespace-nowrap text-sm ${selectedMondayCategory === cat.id ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-slate-100'}`}
                    data-testid={`monday-category-tab-${cat.id}`}>
                    {cat.name.split(':')[0]}
                  </Button>
                ))}
              </div>
            </div>

            {getMondayProducts.length > 0 ? renderProductGrid(getMondayProducts) : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No products available in this category</p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Wednesday Meals */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <Button variant="ghost" onClick={() => setStep(2)} className="text-slate-600" data-testid="back-to-step-2">
                <ChevronLeft className="w-4 h-4 mr-1 sm:mr-2" />Monday
              </Button>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="text-right">
                  <p className="text-xs sm:text-sm text-slate-500">Wednesday items</p>
                  <p className="text-lg sm:text-xl font-semibold text-slate-900">{wednesdayItemCount}</p>
                </div>
                <Button onClick={() => setStep(4)} disabled={totalItems === 0}
                  className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200 text-sm" data-testid="proceed-to-step-4">
                  Add-on's<ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-700 font-semibold">W</span>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Select Meals - Wednesday Delivery</h2>
                <p className="text-sm text-slate-500">{deliveryWeek ? format(new Date(deliveryWeek.getTime() + 2 * 86400000), "EEEE, d MMMM yyyy") : ''}</p>
              </div>
            </div>

            <div className="overflow-x-auto pb-2">
              <div className="flex gap-2 min-w-max">
                {wednesdayCategories.map((cat) => (
                  <Button key={cat.id} variant={selectedWednesdayCategory === cat.id ? "default" : "outline"}
                    onClick={() => setSelectedWednesdayCategory(cat.id)}
                    className={`rounded-full whitespace-nowrap text-sm ${selectedWednesdayCategory === cat.id ? 'bg-green-600 hover:bg-green-700' : 'hover:bg-slate-100'}`}
                    data-testid={`wednesday-category-tab-${cat.id}`}>
                    {cat.name.split(':')[0]}
                  </Button>
                ))}
              </div>
            </div>

            {getWednesdayProducts.length > 0 ? renderProductGrid(getWednesdayProducts) : (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No products available in this category</p>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Add-on's */}
        {step === 4 && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <Button variant="ghost" onClick={() => setStep(3)} className="text-slate-600" data-testid="back-to-step-3">
                <ChevronLeft className="w-4 h-4 mr-1 sm:mr-2" />Wednesday
              </Button>
              <Button onClick={() => setStep(5)}
                className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200 text-sm" data-testid="proceed-to-step-5">
                Review Order<ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Gift className="w-5 h-5 text-amber-700" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Add-on's</h2>
                <p className="text-sm text-slate-500">Select your included and additional add-ons</p>
              </div>
            </div>

            {/* Allowance Summary Card */}
            <Card className="border-green-200 bg-green-50/50 shadow-sm">
              <CardContent className="p-4 sm:p-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm font-medium text-slate-700">Chilled/Hybrid meals ordered:</span>
                    <Badge className="bg-green-100 text-green-800 text-sm">{chilledHybridCount} meals</Badge>
                  </div>
                  
                  {cateringPlan === 'Large Meals' && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Choose your included add-on type:</p>
                      <div className="grid grid-cols-2 gap-3">
                        <button onClick={() => { setAddonPreference('basic'); setAddonQuantities({}); }}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${addonPreference === 'basic' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid="addon-pref-basic">
                          <p className="font-medium text-sm">Basic Add-ons</p>
                          <p className="text-xs text-slate-500 mt-1">2 per chilled meal = <strong>{chilledHybridCount * 2}</strong> total</p>
                        </button>
                        <button onClick={() => { setAddonPreference('premium'); setAddonQuantities({}); }}
                          className={`p-3 rounded-xl border-2 text-left transition-all ${addonPreference === 'premium' ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid="addon-pref-premium">
                          <p className="font-medium text-sm">Premium Add-ons</p>
                          <p className="text-xs text-slate-500 mt-1">1 per chilled meal = <strong>{chilledHybridCount * 1}</strong> total</p>
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t border-green-200">
                    <span className="font-medium text-sm text-green-800">
                      Included {includedAddonAllowance.type} add-ons:
                    </span>
                    <span className="font-bold text-green-800">
                      {totalIncludedSelected} / {includedAddonAllowance.count}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Included Add-ons Selection */}
            {includedAddonAllowance.count > 0 && (
              <Card className="border-slate-200/60 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Select Included {includedAddonAllowance.type === 'basic' ? 'Basic' : 'Premium'} Add-ons
                  </CardTitle>
                  <CardDescription>
                    {includedAddonAllowance.type === 'basic' 
                      ? `Choose up to 2 types. ${includedAddonAllowance.count - totalIncludedSelected} remaining.`
                      : `Choose 1 type — all ${includedAddonAllowance.count} will be this type.`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {includedAddonAllowance.type === 'basic' ? (
                    // Basic: quantity selection, max 2 types
                    addons.basic.map((addonName) => {
                      const qty = addonQuantities[addonName] || 0;
                      return renderAddonRow(addonName, qty, handleAddonQuantityChange, handleAddonQuantityInput, 'basic', 2, selectedBasicTypes, true);
                    })
                  ) : (
                    // Premium: just pick 1 type, auto-assigns full allowance
                    addons.premium.map((addonName) => {
                      const isSelected = addonQuantities[addonName] > 0;
                      return (
                        <button key={addonName} type="button"
                          onClick={() => {
                            // Clear other selections and set this one to full allowance
                            const newQtys = {};
                            newQtys[addonName] = includedAddonAllowance.count;
                            setAddonQuantities(newQtys);
                          }}
                          className={`w-full flex items-center justify-between p-3 rounded-xl border-2 transition-all text-left ${
                            isSelected ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid={`premium-addon-select-${addonName.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <div>
                            <p className="font-medium text-sm text-slate-900">{addonName}</p>
                            <p className="text-xs text-slate-500">Premium add-on</p>
                          </div>
                          {isSelected && (
                            <Badge className="bg-green-100 text-green-800 text-xs">
                              x{includedAddonAllowance.count} included
                            </Badge>
                          )}
                        </button>
                      );
                    })
                  )}
                </CardContent>
              </Card>
            )}

            {/* Additional Add-ons */}
            <Card className="border-slate-200/60 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Additional Add-ons (Extra)
                </CardTitle>
                <CardDescription>
                  Want extra add-ons beyond your included allowance? Select below.
                  {totalAdditionalSelected > 0 && <span className="text-amber-600 font-medium ml-1">{totalAdditionalSelected} extra selected</span>}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Additional Basic */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-700">Basic Add-ons (max 2 types)</p>
                  {addons.basic.map((addonName) => {
                    const qty = additionalAddonQuantities[addonName] || 0;
                    return renderAddonRow(addonName, qty, handleAdditionalAddonChange, handleAdditionalAddonInput, 'basic', 2, additionalBasicTypes, true);
                  })}
                </div>
                {/* Additional Premium */}
                <div className="space-y-2 pt-3 border-t border-slate-100">
                  <p className="text-sm font-medium text-slate-700">Premium Add-ons (max 1 type)</p>
                  {addons.premium.map((addonName) => {
                    const qty = additionalAddonQuantities[addonName] || 0;
                    return renderAddonRow(addonName, qty, handleAdditionalAddonChange, handleAdditionalAddonInput, 'premium', 1, additionalPremiumTypes, true);
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Allergy & Dietary Notes */}
            <Card className="border-slate-200/60 shadow-sm">
              <CardHeader className="pb-3">
                <CardTitle className="text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>Allergy & Dietary Notes</CardTitle>
                <CardDescription>Specify any allergies or dietary requirements for specific meals</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea value={orderNotes} onChange={(e) => setOrderNotes(e.target.value)}
                  placeholder="e.g., Mixed Cheese Sandwich - Dairy Free"
                  className="rounded-xl min-h-[80px]" data-testid="order-notes-input" />
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={() => setStep(5)}
                className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200" data-testid="proceed-to-confirmation">
                Review Order<ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && (
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <Button variant="ghost" onClick={() => setStep(4)} className="text-slate-600" data-testid="back-to-step-4">
                <ChevronLeft className="w-4 h-4 mr-1 sm:mr-2" />Add-on's
              </Button>
            </div>

            <div className="text-center mb-2">
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Review Your Order</h2>
              <p className="text-sm text-slate-500 mt-1">Please check everything before submitting</p>
            </div>

            {/* Order Info */}
            <Card className="border-slate-200/60 shadow-sm">
              <CardContent className="p-4 sm:p-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Delivery Week:</span>
                  <span className="font-medium">{deliveryWeek ? format(deliveryWeek, 'EEEE, d MMMM yyyy') : ''}</span>
                </div>
                {menuWeek && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Menu Week:</span>
                    <Badge className="bg-green-100 text-green-800">Week {menuWeek}</Badge>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Catering Plan:</span>
                  <span className="font-medium">{cateringPlan}</span>
                </div>
                {user?.hte_meal_type && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">HTE Preference:</span>
                    <span className="font-medium">{user.hte_meal_type}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Monday Items */}
            {getMondayItems.length > 0 && (
              <Card className="border-slate-200/60 shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 font-semibold text-xs">M</span>
                    </div>
                    <CardTitle className="text-base">Monday Delivery ({mondayItemCount} items)</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {getMondayItems.map((item) => (
                      <div key={item.product_id} className="px-4 sm:px-6 py-3 flex justify-between items-center">
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{item.product_name}</p>
                          <p className="text-xs text-slate-500">{item.category_name.split(':')[0]}</p>
                        </div>
                        <Badge variant="secondary" className="flex-shrink-0">x{item.quantity}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Wednesday Items */}
            {getWednesdayItems.length > 0 && (
              <Card className="border-slate-200/60 shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-700 font-semibold text-xs">W</span>
                    </div>
                    <CardTitle className="text-base">Wednesday Delivery ({wednesdayItemCount} items)</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {getWednesdayItems.map((item) => (
                      <div key={item.product_id} className="px-4 sm:px-6 py-3 flex justify-between items-center">
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-slate-900 truncate">{item.product_name}</p>
                          <p className="text-xs text-slate-500">{item.category_name.split(':')[0]}</p>
                        </div>
                        <Badge variant="secondary" className="flex-shrink-0">x{item.quantity}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add-ons Summary */}
            {(totalIncludedSelected > 0 || totalAdditionalSelected > 0) && (
              <Card className="border-slate-200/60 shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
                      <Gift className="w-3 h-3 text-amber-700" />
                    </div>
                    <CardTitle className="text-base">Add-on's</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-slate-100">
                    {Object.entries(addonQuantities).filter(([, q]) => q > 0).map(([name, qty]) => (
                      <div key={name} className="px-4 sm:px-6 py-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-sm text-slate-900">{name}</p>
                          <p className="text-xs text-green-600">Included</p>
                        </div>
                        <Badge variant="secondary">x{qty}</Badge>
                      </div>
                    ))}
                    {Object.entries(additionalAddonQuantities).filter(([, q]) => q > 0).map(([name, qty]) => (
                      <div key={name} className="px-4 sm:px-6 py-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-sm text-slate-900">{name}</p>
                          <p className="text-xs text-amber-600">Additional</p>
                        </div>
                        <Badge className="bg-amber-100 text-amber-800">x{qty}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Allergy & Dietary Notes */}
            {orderNotes && (
              <Card className="border-amber-200 bg-amber-50/50 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Allergy & Dietary Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-700 whitespace-pre-line">{orderNotes}</p>
                </CardContent>
              </Card>
            )}

            {/* Submit */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(4)} className="rounded-full flex-1 sm:flex-none">
                <ChevronLeft className="w-4 h-4 mr-2" />Back to Add-on's
              </Button>
              <Button onClick={handleSubmitOrder} disabled={submitting}
                className="rounded-full bg-green-600 hover:bg-green-700 active:scale-95 transition-all duration-200 flex-1" data-testid="submit-order-button">
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{editingOrderId ? 'Updating...' : 'Submitting...'}</>
                ) : (
                  <><Check className="w-4 h-4 mr-2" />{editingOrderId ? 'Update Order' : 'Submit Order'}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </ClientLayout>
  );
};

export default OrderForm;
